import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SectionLabel, Reveal } from "./Reveal";

export const HOME_FAQS = [
  {
    q: "How does Bottom Line Improvements work?",
    a: "You start by telling us a little about your business and the areas you'd like to explore. We review your interests and connect you with independent specialists who focus on those areas. The specialists evaluate your situation and explain any options directly with you.",
  },
  {
    q: "Is there a cost to explore these opportunities?",
    a: "There's no cost or obligation to start a conversation with us. If a specialist engagement makes sense for your business, they'll explain how their services work — including any costs — before you decide anything.",
  },
  {
    q: "Are business savings guaranteed?",
    a: "No. Every business is different, and any savings or benefits depend on your circumstances. Specialists evaluate your situation and explain what may be possible before you make any decision.",
  },
  {
    q: "Do I have to change my current providers?",
    a: "No. We're not here to replace the professionals you already trust. Many owners explore opportunities simply to confirm their current setup is working well.",
  },
  {
    q: "Can I explore several opportunities at once?",
    a: "Yes. You can select as many areas of interest as you like — expenses, technology, benefits, and tax incentives — and we'll help you connect with relevant specialists for each.",
  },
  {
    q: "Who actually performs the evaluations?",
    a: "Independent specialists with experience in each service area. Bottom Line Improvements is not an insurance provider, accounting practice, or tax advisory firm — we help connect you with the right resources.",
  },
  {
    q: "Will I be pressured to purchase anything?",
    a: "No. You stay in control at every step. Our role is to help you find what matters; you decide whether any recommendation makes sense for your business.",
  },
  {
    q: "Why did Community Promotions launch this service?",
    a: "After more than 25 years of helping businesses grow, we saw that improving profitability often isn't about another sale — it's about finding opportunities already inside the business. Bottom Line Improvements was created to share those resources.",
  },
];

function Item({ item, index, open, onToggle }) {
  return (
    <div className="border-b border-line">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-6 py-5 md:py-6 text-left group"
      >
        <span className={`font-display font-bold tracking-tight text-[16px] md:text-lg transition-colors ${open ? "text-teal-dark" : "text-navy group-hover:text-royal"}`}>
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.25 }}
          className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${open ? "bg-teal border-teal text-navy" : "border-line text-navy"}`}
        >
          <Plus className="w-4 h-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-6 pr-12 text-[15px] leading-relaxed text-slate">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ({ items = HOME_FAQS, title = "Questions, Answered Straight", label = "FAQ", dark = false }) {
  const [open, setOpen] = useState(0);
  return (
    <section className={`relative py-16 md:py-24 ${dark ? "bg-navy-950" : "bg-white"}`} data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionLabel dark={dark}>{label}</SectionLabel>
          <Reveal>
            <h2 className={`mt-5 font-display font-extrabold tracking-tightest text-3xl sm:text-4xl leading-[1.1] ${dark ? "text-white" : "text-navy"}`}>
              {title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`mt-4 text-[15px] leading-relaxed ${dark ? "text-ice/65" : "text-slate"}`}>
              Straight answers about how we work, what things cost, and who does what.
            </p>
          </Reveal>
        </div>
        <div className={`lg:col-span-8 border-t ${dark ? "border-white/15" : "border-line"}`}>
          {items.map((item, i) => (
            <Item key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
